import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import bodyMap from '../assets/rehabo-mapa-tela.webp'
import './RehaboScene.css'

/*
 * Animovaná scéna z Rehaba (návšteva v rozvrhu → detail → popis, zákres, platba, uloženie), štýly v RehaboScene.css.
 * Dekoratívna (aria-hidden); texty sú zámerne slovenské, je to miniatúra skutočného rozhrania Rehaba.
 * JavaScript rieši len to, čo CSS nevie: mierku, šírku písaných riadkov a pauzu mimo obrazovky alebo na želanie.
 */
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

const chip = (left: number, top: number, width: number, background: string): CSSProperties => ({ left, top, width, background })
const CURSOR = <path d="M1 1l13 6.5-5.6 1.6L5.6 15z" fill="#1F3B38" stroke="#fff" strokeWidth="1.4" strokeLinejoin="round" />

export default function RehaboScene() {
  const { t } = useTranslation()
  const sceneRef = useRef<HTMLDivElement>(null)
  const [reduced, setReduced] = useState(() => typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION).matches)
  const [visible, setVisible] = useState(false)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    const scene = sceneRef.current
    if (!scene) return
    const fit = scene.querySelector<HTMLElement>('.hs-fit')!

    // Vnútro má pevných 588 px a zmenšuje sa ako jeden kus
    const scale = () => scene.style.setProperty('--scale', String(fit.clientWidth / 588))
    scale()
    const ro = 'ResizeObserver' in window ? new ResizeObserver(scale) : null
    if (ro) ro.observe(fit)
    else window.addEventListener('resize', scale, { passive: true })

    // Šírka písaného riadku podľa skutočného písma; jeden krok animácie = jeden znak
    const measure = () => {
      scene.querySelectorAll<HTMLElement>('[data-type]').forEach(el => {
        const w = el.scrollWidth
        if (!w) return
        el.style.setProperty('--tw', `${w + 1}px`)
        el.style.animationTimingFunction = `steps(${Math.max(1, el.textContent!.trim().length)}, end)`
      })
    }
    if (document.fonts?.ready) document.fonts.ready.then(measure)
    else measure()

    // Mimo obrazovky slučka nebeží
    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver(entries => setVisible(entries[0].isIntersecting), { threshold: 0.15 })
      : null
    if (io) io.observe(scene)
    else setVisible(true)

    const mq = typeof window.matchMedia === 'function' ? window.matchMedia(REDUCED_MOTION) : null
    const onMotionChange = (e: MediaQueryListEvent) => setReduced(e.matches)
    mq?.addEventListener('change', onMotionChange)

    return () => {
      ro?.disconnect()
      if (!ro) window.removeEventListener('resize', scale)
      io?.disconnect()
      mq?.removeEventListener('change', onMotionChange)
    }
  }, [])

  // Pri obmedzenom pohybe sa --play nenastavuje: inline hodnota by prebila media query v CSS a scéna by bežala
  useEffect(() => {
    const scene = sceneRef.current
    if (!scene) return
    if (reduced) scene.style.removeProperty('--play')
    else scene.style.setProperty('--play', visible && !paused ? 'running' : 'paused')
  }, [reduced, visible, paused])

  return (
    <div className="w-full max-w-[588px]">
      <div className="hs" ref={sceneRef} aria-hidden="true">
        <div className="hs-fit">
          <div className="hs-scale">
            <div className="hs-win">
              <div className="hs-chrome">
                <span className="hs-dot" style={{ background: '#F87171' }} />
                <span className="hs-dot" style={{ background: '#FBBF24' }} />
                <span className="hs-dot" style={{ background: '#34D399' }} />
                <span className="hs-url">rehabo.sk/app/calendar/schedule</span>
              </div>
              <div className="hs-view">
                <div className="hs-cal">
                  <div className="hs-head">
                    <div className="hs-title">Rozvrh · 3. – 9. 8. 2026</div>
                    <div className="hs-pills">
                      <span className="hs-pill hs-clock"><i />10:15</span>
                      <span className="hs-pill">2 miestnosti</span>
                      <span className="hs-pill">3 terapeuti</span>
                    </div>
                  </div>
                  <div className="hs-grid">
                    <div className="hs-cols">
                      <b>Miestnosť</b><span>8</span><span>9</span><span>10</span><span>11</span><span>12</span><span>13</span><span>14</span><span>15</span>
                    </div>
                    <div className="hs-body">
                      <div className="hs-rows"><span>M1 · Pon</span><span>M1 · Uto</span><span>M2 · Pon</span><span>M2 · Uto</span></div>
                      <span className="hs-chip" style={chip(80, 7, 52, '#5FA79E')}>Jana K.</span>
                      <span className="hs-chip" style={chip(140, 7, 56, '#5FA79E')}>Martin B.</span>
                      <span className="hs-chip" style={chip(245, 7, 66, '#8B80D8')}>Tomáš K.</span>
                      <span className="hs-chip" style={chip(110, 43, 50, '#8B80D8')}>Eva H.</span>
                      <span className="hs-chip" style={chip(350, 43, 62, '#8B80D8')}>Ondrej Š.</span>
                      <span className="hs-chip" style={chip(170, 79, 66, '#5FA79E')}>Zuzana N.</span>
                      <span className="hs-chip" style={chip(406, 79, 72, '#5FA79E')}>Katarína B.</span>
                      <span className="hs-chip hs-new" style={chip(200, 115, 54, '#3E948B')}>Petra M.</span>
                      <div className="hs-now"><i /></div>
                      <div className="hs-ring" />
                      <div className="hs-done">Zaplatené</div>
                    </div>
                  </div>
                  <svg className="hs-cur hs-cur1" width="16" height="19" viewBox="0 0 16 19">{CURSOR}</svg>
                </div>

                <div className="hs-card">
                  <div className="hs-inner">
                    <div className="hs-dhead">
                      <span className="hs-dsq" />
                      <span className="hs-dname">Tomáš K.</span>
                      <span className="hs-dmeta">Pon 3. 8. · 11:00 – 12:00 · M1 · Fyzioterapia 60′</span>
                      <svg className="hs-dx" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                    </div>
                    <div className="hs-dbody">
                      <div className="hs-desc">
                        <div className="hs-lab">Popis návštevy</div>
                        <div className="hs-box">
                          <div className="hs-tools"><b>B</b><i>I</i><u>U</u><span className="hs-div" /><span>≡</span><span>≔</span><span className="hs-tpl">Vyberte šablónu</span></div>
                          <div className="hs-line hs-t1" data-type="">Bolesť driekovej chrbtice, obmedzený rozsah.</div>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div className="hs-line hs-t2" data-type="">Mäkké techniky, mobilizácia L4/L5.</div>
                            <div className="hs-caret" />
                          </div>
                        </div>
                      </div>
                      <div className="hs-draw">
                        <div className="hs-lab">Zákres</div>
                        <div className="hs-dbox">
                          <div className="hs-dtools">
                            <span className="hs-tool hs-pen">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="M2 2l7.586 7.586" /></svg>
                            </span>
                            <span className="hs-tool hs-cross">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                            </span>
                            <span className="hs-div" />
                            <span className="hs-swatch" />
                            <span className="hs-dlab">Zákres</span>
                          </div>
                          <div className="hs-canvas">
                            {/* Bez loading="lazy": detail je pri načítaní orezaný na 66 × 22 px, prehliadač by obrázok odložil */}
                            <img src={bodyMap} alt="" width={150} height={128} decoding="async" />
                            <svg viewBox="0 0 150 128" preserveAspectRatio="none">
                              <path className="hs-ink hs-ink1" d="M101 58c-9-5-15 3-11 10 4 8 17 9 23 3 5-6 2-16-7-18" />
                              <path className="hs-ink hs-ink2" d="M39 50l13 13M52 50l-13 13" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="hs-foot">
                      <div className="hs-svc"><span>Fyzioterapia 60′</span><b>45,00 €</b></div>
                      <div className="hs-pay">
                        <div className="hs-paybtn">Zaplatiť kartou</div>
                        <div className="hs-paid">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                          Zaplatené 45,00 €
                        </div>
                      </div>
                      <div className="hs-save">Uložiť</div>
                    </div>
                    <div className="hs-toast">
                      <i><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg></i>
                      Zmeny boli úspešne uložené
                    </div>
                  </div>
                </div>

                <svg className="hs-cur hs-cur2" width="16" height="19" viewBox="0 0 16 19">{CURSOR}</svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slučka beží dlhšie ako 5 s, preto ju musí ísť zastaviť (WCAG 2.2.2). Pri obmedzenom pohybe stojí sama. */}
      {!reduced && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={() => setPaused(p => !p)}
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-xs text-slate-100/80 hover:bg-white/15 hover:text-white transition-colors"
          >
            <svg aria-hidden="true" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
              {paused ? <path d="M6.3 2.84A1.5 1.5 0 0 0 4 4.11v11.78a1.5 1.5 0 0 0 2.3 1.27l9.34-5.89a1.5 1.5 0 0 0 0-2.54L6.3 2.84Z" /> : <path d="M5.75 3a.75.75 0 0 0-.75.75v12.5c0 .41.34.75.75.75h1.5a.75.75 0 0 0 .75-.75V3.75A.75.75 0 0 0 7.25 3h-1.5Zm7 0a.75.75 0 0 0-.75.75v12.5c0 .41.34.75.75.75h1.5a.75.75 0 0 0 .75-.75V3.75a.75.75 0 0 0-.75-.75h-1.5Z" />}
            </svg>
            {paused ? t('a11y.anim_play') : t('a11y.anim_pause')}
          </button>
        </div>
      )}
    </div>
  )
}
